import { useEffect } from "react";

/** Pull image files out of a clipboard/drop DataTransfer (screenshots, copied images). */
export function imageFilesFromClipboard(dt: DataTransfer | null): File[] {
  if (!dt) return [];
  const files: File[] = [];
  for (const item of Array.from(dt.items ?? [])) {
    if (item.kind === "file") {
      const file = item.getAsFile();
      if (file && file.type.startsWith("image/")) files.push(file);
    }
  }
  // Safari/Firefox sometimes populate only `.files`.
  if (files.length === 0) {
    for (const file of Array.from(dt.files ?? [])) {
      if (file.type.startsWith("image/")) files.push(file);
    }
  }
  return files;
}

/**
 * Fires `onFiles` with any image files on a ⌘V/paste while `enabled`. Plain text
 * pastes carry no files, so this never hijacks typing — it only acts on a pasted
 * image (e.g. a screenshot) and otherwise lets the event through.
 */
export function useFilePaste(onFiles: (files: File[]) => void, enabled = true): void {
  useEffect(() => {
    if (!enabled) return;
    function onPaste(event: ClipboardEvent) {
      const files = imageFilesFromClipboard(event.clipboardData);
      if (files.length > 0) {
        event.preventDefault();
        onFiles(files);
      }
    }
    document.addEventListener("paste", onPaste);
    return () => document.removeEventListener("paste", onPaste);
  }, [onFiles, enabled]);
}
